import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Search as SearchIcon, X } from 'lucide-react';
import {
  useResolvePrePopulatedOption,
  useSearchOptions,
} from '@/features/user-management/onboarding/form-builder';
import type { FormSearchMeta } from '@/features/user-management/onboarding/form-builder';
import type { FieldEditMeta } from '../../types/resource';

interface SearchOptionInputProps {
  /**
   * Field-edit meta from the resource meta (`field.edit`). The
   * embedded `search_meta` mirrors the onboarding `FormSearchMeta`
   * shape 1:1 — same field names, same semantics — so we hand it
   * straight to the shared hooks without a translation layer.
   */
  editMeta: FieldEditMeta;
  /**
   * Current wire value (typically a numeric or string primary key —
   * `beneficiary_id`, `master_location_lookup_id`, etc.). Rendered as
   * a friendly label once resolved.
   */
  value: unknown;
  onChange: (next: unknown) => void;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Reviewer-facing picker for `uidt: "search"` fields. Reuses the
 * onboarding form-builder's `useSearchOptions` +
 * `useResolvePrePopulatedOption` hooks so all `/utilities/options/*`
 * traffic goes through the same debounced React Query pipeline as
 * the mobile-form Search widget — meaning:
 *
 *   - one canonical source of truth for lookup logic
 *   - consistent debounce / caching / stale-time across the app
 *   - if the underlying options endpoint gains a new param
 *     (`search_space`, `attachment`, …), we inherit it for free
 *
 * The onboarding `UserOptionPicker` is a slightly different UX
 * (user email selector for a filter chip). This component is the
 * form-field variant — larger tap targets, dialog-safe outside-click
 * handling, and a Clear affordance that respects `required`.
 */
export function SearchOptionInput({
  editMeta,
  value,
  onChange,
  required,
  disabled,
}: SearchOptionInputProps) {
  // Convert the review meta's `search_meta` into the shape the shared
  // hooks expect. The two are structurally identical; the cast keeps
  // the types honest without a lossy runtime transformation.
  const searchMeta = useMemo<FormSearchMeta | undefined>(() => {
    const raw = editMeta.search_meta;
    if (!raw) return undefined;
    return {
      options_id: raw.options_id,
      key: raw.key,
      primary_field: raw.primary_field,
      search_column: raw.search_column,
      specific_type: raw.specific_type ?? null,
      secondary_fields: raw.secondary_fields,
    };
  }, [editMeta]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Label from the most recent pick; wins over the resolved label so
  // the UI stays in sync with the user's action even before the next
  // resolveQuery completes.
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Resolve the current stored value → a display row (once when the
  // component mounts, or when `value` changes).
  const resolveQuery = useResolvePrePopulatedOption({
    searchMeta,
    prePopulatedValue: toPrimitiveValue(value),
  });

  // Fires only while the dropdown is open; debounced internally.
  const searchQuery = useSearchOptions({
    searchMeta,
    searchValue: query,
    enabled: open,
  });

  // Clear the manual label whenever the parent resets the value to
  // something empty (e.g. via Clear filters, or when a stale-record
  // reset repopulates the row).
  useEffect(() => {
    if (value === null || value === undefined || value === '') {
      setPickedLabel(null);
    }
  }, [value]);

  // Close on outside click — matches every other picker in the app.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const displayValue = value === null || value === undefined || value === '' ? '' : String(value);
  const displayLabel =
    pickedLabel ??
    resolveQuery.data?.primary ??
    (displayValue ? `#${displayValue}` : '');
  const displaySecondary =
    pickedLabel !== null
      ? undefined
      : resolveQuery.data?.secondary || undefined;

  const showClear = Boolean(displayValue) && !required && !disabled;

  const options = searchQuery.data ?? [];
  const isSearching = searchQuery.isLoading || searchQuery.isFetching;
  const searchError = searchQuery.error as Error | undefined;

  const handleClear = () => {
    onChange(null);
    setPickedLabel(null);
    setQuery('');
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-rgf-gray-300 bg-white px-3 py-2 text-left text-sm text-rgf-gray-900 focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500 disabled:cursor-not-allowed disabled:bg-rgf-gray-50 disabled:text-rgf-gray-500"
      >
        <span className="min-w-0 flex-1 truncate">
          {displayLabel ? (
            <>
              <span className="block truncate">{displayLabel}</span>
              {displaySecondary && (
                <span className="mt-0.5 block truncate text-xs text-rgf-gray-500">
                  {displaySecondary}
                </span>
              )}
            </>
          ) : (
            <span className="text-rgf-gray-400">— Select —</span>
          )}
        </span>
        <span className="flex flex-shrink-0 items-center gap-1 text-rgf-gray-400">
          {resolveQuery.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {showClear && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
              className="rounded p-0.5 hover:bg-rgf-gray-100 hover:text-rgf-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }
              }}
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-rgf-gray-200 bg-white shadow-lg">
          <div className="px-2 pt-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-rgf-gray-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to search…"
                className="w-full rounded-md border border-rgf-gray-200 py-1.5 pl-8 pr-3 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-2">
            {isSearching ? (
              <div className="px-3 py-2 text-sm text-rgf-gray-500">Loading…</div>
            ) : searchError ? (
              <div className="px-3 py-2 text-sm text-red-600">
                {searchError.message || 'Failed to load options.'}
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-rgf-gray-500">No matches.</div>
            ) : (
              options.map((row) => (
                <button
                  key={String(row.value)}
                  type="button"
                  onClick={() => {
                    onChange(row.value);
                    setPickedLabel(row.primary || String(row.value));
                    setQuery('');
                    setOpen(false);
                  }}
                  className="block w-full px-3 py-2 text-left hover:bg-rgf-gray-50"
                >
                  <div className="text-sm text-rgf-gray-900">
                    {row.primary || '—'}
                  </div>
                  {row.secondary && (
                    <div className="mt-0.5 text-xs text-rgf-gray-500">
                      {row.secondary}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * `useResolvePrePopulatedOption` accepts only `string | number | null
 * | undefined`. Coerce whatever the row happens to hold down to that
 * — object / array values (which shouldn't reach a search widget but
 * do exist in the wider row shape) get flattened to a string form so
 * the query still fires with something intelligible.
 */
function toPrimitiveValue(value: unknown): string | number | null | undefined {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'boolean') return String(value);
  return String(value);
}
